import { useNavigate } from "react-router-dom";

import type { SessionInferenceMode } from "@/components/graphql/schema.graphql";
import type { InferenceFormNavigationState } from "@/components/inference-forms/constants";
import { ReferenceMeshType } from "@/components/ui-v2/References/types";
import type { FileReferenceType } from "@/types";

import type { ActivateAsset, ActivateFile } from "../types";
import {
  type ConvertToImageParams,
  convertToRefImage,
  createVideoReference,
} from "../utils";
import {
  REFINEMENT_VALIDATORS,
  type RefinementValidatorInput,
} from "./constants";

type NavigateFn = ReturnType<typeof useNavigate>;

export const prepareSourceVideos = (
  sources: ActivateFile[],
): FileReferenceType[] =>
  sources
    .map((source) => createVideoReference(source))
    .filter((ref): ref is FileReferenceType => ref !== undefined);

export const prepareSourceMeshes = (
  sources: ActivateFile[],
): ReferenceMeshType[] =>
  sources
    .filter((source) => !!source.url)
    .map((source) => ({ src: source.url, driveFileId: source.id }));

export const applyValidator = <T extends RefinementValidatorInput>(
  items: T[],
  mode: SessionInferenceMode,
): T[] => {
  const validator = REFINEMENT_VALIDATORS[mode];
  return validator ? items.filter(validator) : items;
};

export const prepareSourceImages = async (
  sources: (ActivateFile | ActivateAsset)[],
  convertAssetSnapshotToFile: ConvertToImageParams["convertAssetSnapshotToFile"],
): Promise<FileReferenceType[]> => {
  const results = await Promise.all(
    sources.map((source) =>
      convertToRefImage({ source, convertAssetSnapshotToFile }),
    ),
  );
  return results.filter((img): img is FileReferenceType => img !== undefined);
};

export const navigateToForm = (
  mode: SessionInferenceMode,
  path: string,
  formValues: Record<string, unknown>,
  deps: {
    navigate: NavigateFn;
    showSuccess: (msg: string) => void;
  },
): void => {
  const { navigate, showSuccess } = deps;
  navigate(path, {
    state: { mode, form: formValues } satisfies InferenceFormNavigationState,
  });
  showSuccess("Ready to edit selected files.");
};
